import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useEffect, useMemo, useRef } from "react";
import { Application } from "shared/types";
import { STEPS, AX_ORIGIN } from "./constants";
import { Step } from "./types";
import { normalizeSchema } from "./helpers";
import { useAuthContext } from "providers/Auth";
import { patchApplicantStep } from "shared/utils";

export const useApplicant = ({
  applicationId,
  applicantId
}: {
  applicationId?: number;
  applicantId?: number;
}) => {
  const applicationMeta = useQuery<Application>({
    queryKey: ["applicationMeta", String(applicationId), String(applicantId)],
    queryFn: () =>
      instance
        .get(`/portal/v1/applications/${applicationId}`)
        .then(({ data }) => data)
  });

  const applicant = applicationMeta.data?.applicants.find(
    (applicant) => applicant.id === Number(applicantId)
  );

  return {
    application: applicationMeta.data,
    applicant,
    isApplicationLoading: applicationMeta.isLoading,
    isApplicationRefreshing: applicationMeta.isRefetching,
    refetchApplication: applicationMeta.refetch
  };
};

export const useGoToFirstUnfinishedStep = ({
  allSteps,
  activeStepName,
  setActiveStepName,
  applicant,
  moveInDate
}: {
  allSteps?: Record<string, Step>;
  activeStepName: string | null;
  setActiveStepName: (stepName: string) => void;
  applicant: Application["applicants"][number];
  moveInDate?: string | null;
}): void => {
  const performed = useRef(false);
  useEffect(() => {
    if (
      allSteps &&
      Object.keys(allSteps).length &&
      !activeStepName &&
      !performed.current
    ) {
      // go to first unfinushed step
      const sortedSteps = Object.keys(allSteps).sort(
        (a, b) => STEPS.indexOf(a) - STEPS.indexOf(b)
      );

      let idx = sortedSteps.findIndex((step) => {
        if (step === "move_in_date") return !moveInDate;

        return Object.keys(allSteps[step].data).length === 0;
      });

      idx = idx === -1 ? 0 : idx;

      performed.current = true;

      setActiveStepName(sortedSteps[idx]);
    }
  }, [allSteps, activeStepName, applicant]);
};

export const useCurrentSteps = ({
  metaVerificationsData,
  metaVerificationsIsFetching,
  hasIncomeSource,
  applicantId,
  applicationId,
  activeStepName,
  configuration
}: {
  metaVerificationsData: any;
  metaVerificationsIsFetching: boolean;
  hasIncomeSource: boolean;
  applicantId: number;
  applicationId: number;
  activeStepName: string | null;
  configuration: {
    preferredSubmissionType?: string;
    preferredCheckoutType?: string;
  };
}) => {
  const getSteps = ({
    verificationsData,
    hasIncomeSource,
    applicantId,
    applicationId
  }: {
    verificationsData: any;
    hasIncomeSource: boolean;
    applicantId: number;
    applicationId: number;
  }): [Record<string, Step>, string[]] => {
    if (!verificationsData) return [{}, []];

    const rawSteps: Step[] = [];

    rawSteps.push(
      ...(verificationsData.flatMap(
        (verification: any) =>
          verification.steps?.map((step: Step) => ({
            ...step,
            ...verification,
            name: step.name
          })) ?? []
      ) as Step[])
    );

    const mergedSteps: Record<string, Step> = {};
    rawSteps.forEach((step) => {
      mergedSteps[step.name] = step;
    });

    // Add mock Checkout step
    mergedSteps.checkout = {
      id: 0,
      verification_id: 0,
      name: "Checkout",
      data: {},
      json_schema: {},
      ui_schema: {},
      prefilled_data: {},
      applicant_id: Number(applicantId),
      application_id: Number(applicationId)
    };

    // Insert desired move_in_date step before personal_details
    const personalDetailsStep = mergedSteps["personal_details"];
    const moveInDateStep = {
      id: 0,
      verification_id: 0,
      name: "Move in date",
      data: {},
      json_schema: {},
      ui_schema: {},
      prefilled_data: {},
      applicant_id: Number(applicantId),
      application_id: Number(applicationId)
    };

    const stepsOrder = Object.keys(mergedSteps);
    const personalDetailsIndex = stepsOrder.indexOf("personal_details");
    if (personalDetailsIndex !== -1)
      stepsOrder.splice(personalDetailsIndex, 0, "move_in_date");

    mergedSteps["move_in_date"] = moveInDateStep;
    mergedSteps["personal_details"] = personalDetailsStep;

    // Normalize schemas
    const normalizedSteps: Record<string, Step> = {};
    for (const [key, step] of Object.entries(mergedSteps))
      normalizedSteps[key] = {
        ...step,
        json_schema: normalizeSchema({
          type: "json",
          name: key,
          schema: step.json_schema
        }),
        ui_schema: normalizeSchema({
          type: "ui",
          name: key,
          schema: step.ui_schema
        })
      };

    let sortedStepNames = Object.keys(normalizedSteps).sort(
      (a, b) => STEPS.indexOf(a) - STEPS.indexOf(b)
    );

    if (!hasIncomeSource) {
      sortedStepNames = sortedStepNames.filter(
        (name) => name !== "income_documents"
      );
      delete normalizedSteps["income_documents"];
    }

    sortedStepNames = sortedStepNames.filter(
      (name) => name !== "terms_and_conditions_step"
    );
    delete normalizedSteps["terms_and_conditions_step"];

    return [normalizedSteps, sortedStepNames];
  };

  const [allSteps, stepNames] = useMemo(
    () =>
      getSteps({
        verificationsData: metaVerificationsData,
        hasIncomeSource,
        applicantId,
        applicationId
      }),
    [
      metaVerificationsData,
      metaVerificationsIsFetching,
      hasIncomeSource,
      activeStepName // Should listend on activeStepNameChange as well
    ]
  );

  return { allSteps, stepNames, getSteps };
};

export function useIframeMessaging({
  iframeRef,
  activeStepName,
  allSteps,
  onSubmit,
  setLoading,
  partnerNameInSubmissionDisclosure,
  applicationId
}: {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  activeStepName: string | null;
  allSteps: Record<string, Step> | undefined;
  onSubmit: (data: any) => void;
  setLoading: (val: boolean) => void;
  partnerNameInSubmissionDisclosure?: string;
  applicationId: number;
}) {
  const { partner } = useAuthContext();

  useEffect(() => {
    if (!activeStepName || !allSteps?.[activeStepName]) return;

    const injectFormConfigToIframe = () => {
      const step = allSteps[activeStepName];
      if (!iframeRef.current) return;

      const {
        jsonSchema = step.json_schema,
        uiSchema = step.ui_schema,
        data = step.data,
        prefilledData = step.prefilled_data
      } = patchApplicantStep({
        step,
        patchMeta: { isManualApplication: true }
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const normalizedPrefilledData = prefilledData
        ? Object.entries(prefilledData).reduce(
            (acc, [key, value]) => ({
              ...acc,
              [key]: value === null ? undefined : value
            }),
            {} as Record<string, any>
          )
        : {};

      iframeRef.current.contentWindow?.postMessage(
        {
          type: "FormConfig",
          payload: {
            schema: jsonSchema,
            uiSchema: normalizeSchema({
              type: "ui",
              name: activeStepName,
              schema: uiSchema
            }),
            data: Object.keys(data).length ? data : normalizedPrefilledData
          }
        },
        AX_ORIGIN
      );
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== AX_ORIGIN) return;

      const { type, payload } = event.data;

      if (type === "FormReady") injectFormConfigToIframe();

      if (type === "FormSubmit") {
        setLoading(true);
        onSubmit(payload);
      }
    };

    injectFormConfigToIframe();

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [activeStepName, allSteps]);

  useEffect(() => {
    const injectPartnerDataToIframe = () => {
      if (partner)
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: "ApplicationData",
            payload: {
              partnerName: partner.name,
              propertyManagementSystem:
                partner.property_management_system?.name,
              partnerNameInSubmissionDisclosure,
              applicationId: String(applicationId)
            }
          },
          AX_ORIGIN
        );
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== AX_ORIGIN) return;

      const { type } = event.data;

      if (type === "FormReady") injectPartnerDataToIframe();
    };

    injectPartnerDataToIframe();

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [partner, applicationId]);
}
